import React, { forwardRef, PropsWithChildren } from "react";
import type {
	InvalidDeliveryDate,
	InvalidPickupDate,
	ValidDeliveryDate,
	ValidPickupDate,
} from "../../server/StoreInfoRequests";
import { useIntl, type IntlShape } from "react-intl";
import { isAfter, isToday } from "date-fns";
import { isValidDay } from "./DatePickerUtil";
import { formatLocalDate, parseDate } from "../../utils/DateUtil";
import { Tooltip } from "@metro-ui/core";

/* Generic Date Picker Day */
type DatePickerDayStatus = "SELECTED" | "SLOT" | "VALID" | "HOLIDAY" | "INVALID";
type DatePickerDayProps = {
	date: Date;
	status: DatePickerDayStatus;
	onSelectDate?: (date: Date) => void;
	isCustomerSlot?: boolean;
	pickupRef?: React.Ref<HTMLDivElement>;
};

export const DatePickerDay = forwardRef<HTMLDivElement, DatePickerDayProps>(function DatePickerDayWithRef(
	{ date, status, onSelectDate, ...restProps },
	ref,
) {
	const scrollToBottomIfMobile = () => {
		if (document.body.scrollWidth < 1024) {
			window.scrollTo({
				top: document.body.scrollHeight + 100,
				left: 0,
				behavior: "smooth",
			});
		}
	};

	const onClick = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();

		if (status === "VALID" || status === "SLOT") {
			onSelectDate?.(date);
			window.setTimeout(scrollToBottomIfMobile, 100);
		}
	};

	const isTodayClassAddition = isToday(date) ? "isToday" : "";
	return (
		<div
			ref={ref}
			{...restProps}
			key={date.toISOString()}
			className={`in-month ${getStyleFromStatus(status)} ${isTodayClassAddition}`}
			onClick={onClick}
		>
			{formatLocalDate(date, "d")}
		</div>
	);
});

function getStyleFromStatus(status: DatePickerDayStatus): string {
	switch (status) {
		case "SELECTED":
			return "date-selected valid";

		case "SLOT":
			return "customer-delivery-date valid";

		case "VALID":
			return "valid";

		case "HOLIDAY":
			return "store-holiday disabled";

		case "INVALID":
			return "disabled";

		default:
			return "";
	}
}

export const getDeliveryDatePickerTooltipMessage = (info: ValidDeliveryDate | InvalidDeliveryDate, intl: IntlShape) => {
	switch (info.type) {
		case "ValidDate": {
			const cutoffDateTime = parseDate(info.leadTimeCompliantUntil);
			if (isAfter(new Date(), cutoffDateTime))
				return intl.formatMessage(
					{
						id: "ORC_DELIVERY_DATE_STORE_CUTOFF_TIME_TOOLTIP",
					},
					{
						cutoffTime: formatLocalDate(cutoffDateTime, "eeePPp"),
					},
				);
			if (info.isAlternative)
				return intl.formatMessage({ id: "ORC_DELIVERY_DATE_ALTERNATIVE_FOR_STORE_HOLIDAY_TOOLTIP" });
			break;
		}

		case "InvalidDate": {
			if (info.isStoreHoliday) return intl.formatMessage({ id: "ORC_DELIVERY_DATE_STORE_HOLIDAY_TOOLTIP" });
			break;
		}
	}

	return "";
};

/* Pickup Date Picker Day */
type PickupDatePickerDayProps = {
	date: Date;
	info: ValidPickupDate | InvalidPickupDate;
	isSelected: boolean;
	lastValidDate: string;
	onSelectDate?: (date: Date) => void;
	disableDayByTimeSlot?: boolean;
	isPickup: boolean;
	pickupRef?: React.Ref<HTMLDivElement>;
};

export const getPickupToolTipMessage = (
	info: ValidPickupDate | InvalidPickupDate,
	disableDayByTimeSlot: boolean | undefined,
	intl: IntlShape,
) => {
	switch (info.type) {
		case "ValidDate": {
			const cutoffDateTime = parseDate(info.leadTimeCompliantUntil);
			const isLeadTimeCompliant = !isAfter(new Date(), cutoffDateTime);

			const isTimesSlotValid = info.pickupTimeSlots.some((timeSlots) => timeSlots.availableCapacity > 0);

			if (!isLeadTimeCompliant) {
				return intl.formatMessage(
					{
						id: "ORC_DELIVERY_DATE_STORE_CUTOFF_TIME_TOOLTIP",
					},
					{
						cutoffTime: formatLocalDate(cutoffDateTime, "eeePPp"),
					},
				);
			} else if (disableDayByTimeSlot && !isTimesSlotValid) {
				return intl.formatMessage({ id: "ORC_PICKUP_TIME_SLOT_MISSING_TOOLTIP" });
			}

			break;
		}

		case "InvalidDate": {
			if (info.isStoreHoliday) {
				return intl.formatMessage({ id: "ORC_DELIVERY_DATE_STORE_HOLIDAY_TOOLTIP" });
			}

			break;
		}
	}

	return "";
};

export const DatePickerWithSlotsDay = ({
	date,
	info,
	isSelected,
	lastValidDate,
	onSelectDate,
	disableDayByTimeSlot,
	isPickup,
	pickupRef,
	// We need to propagate remaining props, until they hit a <div>,
	// for the tooltips and element keys to work
	...restProps
}: PickupDatePickerDayProps) => {
	const intl = useIntl();
	const tooltipMessage = isPickup
		? getPickupToolTipMessage(info, disableDayByTimeSlot, intl)
		: getDeliveryDatePickerTooltipMessage(info as ValidDeliveryDate | InvalidDeliveryDate, intl);
	const dayIsValid = isValidDay(date, info, isPickup, disableDayByTimeSlot, lastValidDate);

	const getStatus = () => {
		if (info.type === "ValidDate") {
			if (!dayIsValid) {
				return "INVALID";
			} else if (isSelected) {
				return "SELECTED";
			} else if (info.isCustomerSlot && !isAfter(new Date(), parseDate(info.leadTimeCompliantUntil))) {
				return "SLOT";
			} else {
				return "VALID";
			}
		} else {
			if (info.isStoreHoliday) {
				return "HOLIDAY";
			}

			return "INVALID";
		}
	};

	return (
		<PickupDatePickerTooltip tooltipMessage={tooltipMessage}>
			<DatePickerDay
				{...restProps}
				date={date}
				status={getStatus()}
				onSelectDate={dayIsValid ? onSelectDate : () => {}}
				isCustomerSlot={(info as ValidPickupDate).isCustomerSlot}
				pickupRef={pickupRef}
			/>
		</PickupDatePickerTooltip>
	);
};
type PickupDatePickerTooltipProps = {
	tooltipMessage: string;
};
export const PickupDatePickerTooltip = ({
	tooltipMessage,
	children,
}: PropsWithChildren<PickupDatePickerTooltipProps>) => {
	if (!tooltipMessage) return <>{children}</>;
	return (
		<Tooltip id="day-tooltip" position="top" description={tooltipMessage}>
			{children}
		</Tooltip>
	);
};
